import { Component } from "@theme/component";
import { fetchShopifyResources } from "./shopify-resource-fetcher.js";
import { CartUpdateEvent, ThemeEvents } from "./events.js";

/**
 * @typedef {object} BxgyPromotionState
 * @property {object} detail
 * @property {string} detail.id
 * @property {string} detail.name
 * @property {string} detail.type
 * @property {boolean} detail.eligible
 * @property {number} detail.remainingChoices
 * @property {number[]} detail.availableItems
 * @property {object} config
 */

/**
 * @typedef {object} Cart
 * @property {number} total_price
 * @property {string} currency
 */

/**
 * @typedef {object} Product
 * @property {number} id
 * @property {string} title
 * @property {string} image
 * @property {string} description
 * @property {string} variantTitle
 * @property {string[]} optionValues
 */

/** @type {any} */
const w = window;

class BxgyChoiceSelector extends Component {
  constructor() {
    super();
    this.listenersInitialized = false;
    this.promotionId = null;
    this.mode = "gwp";
    this.sampleMaxChoices = 0;

    this.variantCache = new Map();
    this.promotionStates = new Map();
    // this.addGiftButton = null;
    // this.promoRenderTokens = new Map();
    this.handleUpdateBound = this.handleUpdate.bind(this);
    this.addItemBound = this.addItem.bind(this);
    this.handleCartUpdateBound = this.handleCartUpdate.bind(this);
    this.handleInputChangeBound = this.handleInputChange.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.listenersInitialized) return;
    document.addEventListener("bxgy:promotion-update", this.handleUpdateBound);
    this.addGiftButton = this.querySelector(
      ".bxgy-choice-selector-modal__footer .button",
    );
    if (this.addGiftButton) {
      this.addGiftButton.addEventListener("click", this.addItemBound);
    }
    this.addEventListener("change", this.handleInputChangeBound);
    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.handleCartUpdateBound,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      "bxgy:promotion-update",
      this.handleUpdateBound,
    );
    this.addGiftButton = this.querySelector(
      ".bxgy-choice-selector-modal__footer .button",
    );
    if (this.addGiftButton) {
      this.addGiftButton.removeEventListener("click", this.addItemBound);
    }
    this.removeEventListener("change", this.handleInputChangeBound);
    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.handleCartUpdateBound,
    );
    this.listenersInitialized = false;
  }

  handleUpdate(event) {
    const { id } = event.detail;

    // Use specific ID if you want to bind this component to a single promotion
    // Or handle all choice promotions dynamically
    this.promotionId = id;
    this.promotionStates.set(id, { detail: event.detail });
    // if (!this.listenersInitialized) {
    this.renderAllPromotions();
    this.updateSamplesCta();
    this.listenersInitialized = true;
    // }
  }

  openGwp() {
    this.mode = "gwp";
    this.setModeClass();
    this.clearModalContent();
    this.renderAllPromotions();
    this.updateSamplesCta();
    this.updateFooterButtonLabel();
    const dialog = this.querySelector("#bxgy-choice-selector-modal");
    dialog?.showDialog?.();
  }

  openSamples() {
    this.mode = "sample";
    this.setModeClass();
    this.clearModalContent();
    this.renderAllPromotions();
    this.updateSamplesCta();
    this.updateFooterButtonLabel();
    const dialog = this.querySelector("#bxgy-choice-selector-modal");
    dialog?.showDialog?.();
  }

  /**
   * @param {CartUpdateEvent} event
   */
  async handleCartUpdate(event) {
    setTimeout(() => {
      this.renderProgressBars();
      this.updateSamplesCta(event?.detail?.resource);
    }, 500);
  }

  /**
   * @param {any} eventOrVariantId
   * @returns {Promise<void>}
   */
  async addItem(eventOrVariantId) {
    if (eventOrVariantId instanceof Event) {
      eventOrVariantId.preventDefault();
    }

    const selected = Array.from(
      this.querySelectorAll('input[name^="gwp-product-"]:checked'),
    ).filter((input) => !input.disabled);
    if (!selected.length) return;

    const items = selected.map((input) => {
      const promoId = String(input.dataset.promoId || "");
      const promotionState = this.promotionStates.get(promoId);
      const promotionDetail = promotionState?.detail || {};
      const promotionConfig = this.getPromotionConfig(promotionDetail) || {};
      const promotionName =
        promotionDetail.name ||
        promotionConfig.title ||
        promotionConfig.discountName ||
        promotionConfig.name ||
        "";

      return {
        id: input.value,
        quantity: 1,
        properties: {
          _bxgy_promotion: promotionName,
          _bxgy_config_id: promoId,
          _bxgy_reward_type: input.dataset.rewardType,
          _drop_in: "true",
        },
      };
    });

    try {
      if (this.addGiftButton) {
        this.addGiftButton.setAttribute("disabled", "true");
        this.addGiftButton.classList.add("loading");
      }

      const updates = this.getPromoRemovalUpdates(selected);
      if (Object.keys(updates).length) {
        const updateResponse = await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        });
        if (!updateResponse.ok) return;
      }

      const cartOperations = w?.bxgyController?.cartOperations;

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) return;

      // await for add.js to finish and /cart.js to finish
      const result = await response.json();
      // add is done
      // cart fetching

      // // 1) Fetch authoritative cart snapshot
      const cartRes = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });
      const cart = cartRes.ok ? await cartRes.json() : null;

      if (cart) {
        cartOperations.dispatchCartUpdateEvent(cart, "bxgy-choice-selector", {
          addedItems: items,
        });
      }
    } finally {
      if (this.addGiftButton) {
        this.addGiftButton.removeAttribute("disabled");
        this.addGiftButton.classList.remove("loading");
      }

      const dialog = this.querySelector("#bxgy-choice-selector-modal");
      if (dialog && typeof dialog.closeDialog === "function") {
        setTimeout(() => {
          dialog.closeDialog();
        }, 500);
      }
    }
  }

  /**
   * @param {Event} event
   */
  handleInputChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== "radio" && target.type !== "checkbox") return;
    if (!target.name.startsWith("gwp-product-")) return;
    const groupInputs = Array.from(
      this.querySelectorAll(`input[name="${target.name}"]`),
    );
    if (target.type === "radio") {
      groupInputs.forEach((input) => {
        input
          .closest(".bxgy-choice-selector-modal__product-item")
          ?.classList.remove(
            "bxgy-choice-selector-modal__product-item--selected",
          );
      });
    }
    target
      .closest(".bxgy-choice-selector-modal__product-item")
      ?.classList.toggle(
        "bxgy-choice-selector-modal__product-item--selected",
        target.checked,
      );
    this.lastSelectedInput = target;
    this.ensureSampleSelectionLimits(target.name);
    this.updateFooterButtonLabel();
  }

  ensureSampleSelectionLimits(groupName) {
    const inputs = Array.from(
      this.querySelectorAll(`input[name="${groupName}"]`),
    );
    if (!inputs.length) return;

    const detailId = groupName.replace("gwp-product-", "");
    const detail = this.promotionStates.get(detailId)?.detail;
    const maxChoices = detail?.maxChoices || 1;

    if (maxChoices <= 1) {
      inputs.forEach((input) => {
        if (input.disabled) {
          input.disabled = false;
        }
        input
          .closest(".bxgy-choice-selector-modal__product-item")
          ?.querySelector(".bxgy-choice-selector-modal__product-details")
          ?.classList.remove(
            "bxgy-choice-selector-modal__product-details--disabled",
          );
      });
      return;
    }

    const checkedCount = inputs.filter((input) => input.checked).length;
    if (checkedCount >= maxChoices) {
      inputs.forEach((input) => {
        if (!input.checked) {
          input.disabled = true;
          input
            .closest(".bxgy-choice-selector-modal__product-item")
            ?.querySelector(".bxgy-choice-selector-modal__product-details")
            ?.classList.add(
              "bxgy-choice-selector-modal__product-details--disabled",
            );
        }
      });
    } else {
      inputs.forEach((input) => {
        if (input.disabled) {
          input.disabled = false;
          input
            .closest(".bxgy-choice-selector-modal__product-item")
            ?.querySelector(".bxgy-choice-selector-modal__product-details")
            ?.classList.remove(
              "bxgy-choice-selector-modal__product-details--disabled",
            );
        }
      });
    }
  }

  setModeClass() {
    const dialog = this.querySelector("#bxgy-choice-selector-modal");
    if (!dialog) return;
    dialog.classList.toggle(
      "bxgy-choice-selector-modal--samples",
      this.mode === "sample",
    );
  }

  /**
   * Build cart updates to remove existing promo gifts when repeats are not allowed.
   * @param {HTMLInputElement[]} selected
   * @returns {Record<string, number>}
   */
  getPromoRemovalUpdates(selected) {
    const cart = w?.bxgyController?.previousCartState;
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length) return {};

    const selectedByPromo = selected.reduce((acc, input) => {
      const promoId = String(input.dataset.promoId || "");
      if (!promoId) return acc;
      if (!acc[promoId]) acc[promoId] = new Set();
      acc[promoId].add(String(input.value));
      return acc;
    }, {});

    const updates = {};
    items.forEach((item) => {
      const promoId = String(item?.properties?._bxgy_config_id || "");
      if (!promoId || !selectedByPromo[promoId]) return;

      const allowRepeats =
        this.promotionStates.get(promoId)?.detail?.allowRepeats;
      if (allowRepeats !== false) return;

      const isSelected = selectedByPromo[promoId].has(String(item.variant_id));
      if (!isSelected && item.key) {
        updates[item.key] = 0;
      }
    });

    return updates;
  }

  /**
   * @param {Cart} cart
   * @returns {Promise<void>}
   */

  async renderAllPromotions() {
    const productList = this.querySelector(
      ".bxgy-choice-selector-modal__product-list",
    );
    const selectionHeader = this.querySelector(
      ".bxgy-choice-selector-modal__selection-header",
    );
    if (!productList) return;

    const configs = w?.bxgyController?.configs?.configurations || [];
    const cart = w?.bxgyController?.previousCartState;
    if (this.mode === "gwp" && configs.length && cart) {
      this.renderUnlockMessages(configs, cart);
    } else if (this.mode === "sample") {
      const unlockList = this.querySelector(
        ".bxgy-choice-selector-modal__unlock-list",
      );
      if (unlockList) unlockList.innerHTML = "";
    }

    const promos = Array.from(this.promotionStates.values())
      .map((state) => state.detail)
      .filter((detail) => {
        if (!detail || detail.type !== "choice") return false;
        const config = this.getPromotionConfig(detail);
        const discountType = config?.customerGets?.discountType;
        return this.mode === "sample"
          ? discountType === "sample"
          : discountType !== "sample";
      });

    if (!promos.length) {
      productList.innerHTML = "";
      if (selectionHeader) selectionHeader.innerHTML = "";
      return;
    }

    promos.sort(
      (a, b) =>
        this.getPromotionMinimumAmount(a) - this.getPromotionMinimumAmount(b),
    );
    setTimeout(() => {
      this.renderProgressBars();
    });

    if (selectionHeader) {
      const maxChoices = Math.max(
        ...promos.map((detail) => detail.maxChoices || 0),
      );
      const sampleCount = maxChoices || 1;
      this.sampleMaxChoices = this.mode === "sample" ? maxChoices || 0 : 0;
      const sampleLabel = sampleCount === 1 ? "sample" : "samples";
      selectionHeader.innerHTML =
        this.mode === "sample"
          ? `<p class="bxgy-choice-selector-modal__title">Complimentary Samples</p>
             <p class="bxgy-choice-selector-modal__subtitle">Choose up to ${sampleCount} ${sampleLabel}</p>`
          : `<h2>${promos.length > 1 ? "Choose your gifts" : "Choose your gift"}</h2>`;
    }

    this.updateFooterButtonLabel();

    const sections = await Promise.all(
      promos.map((detail) => this.renderPromotion(detail)),
    );
    productList.innerHTML = sections.join("");
  }

  clearModalContent() {
    const productList = this.querySelector(
      ".bxgy-choice-selector-modal__product-list",
    );
    if (productList) productList.innerHTML = "";
    const selectionHeader = this.querySelector(
      ".bxgy-choice-selector-modal__selection-header",
    );
    if (selectionHeader) selectionHeader.innerHTML = "";
    const unlockList = this.querySelector(
      ".bxgy-choice-selector-modal__unlock-list",
    );
    if (unlockList) unlockList.innerHTML = "";
  }

  updateSamplesCta(cart = w?.bxgyController?.previousCartState) {
    const title = document.querySelector(".cart-drawer__samples-title");
    const subtitle = document.querySelector(".cart-drawer__samples-subtitle");
    if (!subtitle || !title) return;

    if (!this.promotionStates.size) return;

    const resolvedCart =
      cart && Array.isArray(cart?.items)
        ? cart
        : w?.bxgyController?.previousCartState;

    const samplePromos = Array.from(this.promotionStates.values())
      .map((state) => state.detail)
      .filter((detail) => {
        if (!detail || detail.type !== "choice") return false;
        const config = this.getPromotionConfig(detail);
        const matchesCustomerBuys = this.cartMatchesCustomerBuys(
          config,
          resolvedCart,
        );
        return (
          config?.customerGets?.discountType === "sample" && matchesCustomerBuys
        );
      });

    const maxChoices = samplePromos.length
      ? Math.max(...samplePromos.map((detail) => detail.maxChoices || 0))
      : 0;
    const sampleCount = maxChoices || 0;
    const sampleLabel = sampleCount === 1 ? "sample" : "samples";
    const samplePromoIds = new Set(
      samplePromos.map((detail) => String(detail.id)),
    );
    const sampleCartItems = Array.isArray(resolvedCart?.items)
      ? resolvedCart.items.filter((item) =>
          samplePromoIds.has(String(item?.properties?._bxgy_config_id)),
        )
      : [];
    const sampleCartItemCount = sampleCartItems.reduce(
      (total, item) => total + (item?.quantity || 0),
      0,
    );

    if (sampleCartItemCount > 0) {
      title.textContent = "Free samples";
      subtitle.textContent = `${sampleCartItemCount}/${sampleCount} ${sampleLabel} added`;
    } else {
      subtitle.textContent = `Choose up to ${sampleCount} ${sampleLabel}`;
      title.textContent = "Add free samples";
    }
    const samplesCta = document.querySelector(".cart-drawer__samples-cta");
    if (sampleCount === 0 || !samplePromos.length) {
      if (samplesCta && !samplesCta.classList.contains("hidden")) {
        samplesCta.classList.add("hidden");
      }
    } else if (samplesCta && samplesCta.classList.contains("hidden")) {
      samplesCta.classList.remove("hidden");
    }
  }

  updateFooterButtonLabel() {
    if (!this.addGiftButton) return;
    if (this.mode !== "sample") {
      this.addGiftButton.textContent = "ADD GIFT TO BAG";
      return;
    }
    const selectedCount = this.querySelectorAll(
      'input[name^="gwp-product-"]:checked',
    ).length;
    const maxChoices = this.sampleMaxChoices || 0;
    this.addGiftButton.textContent = `ADD TO BAG (${selectedCount}/${maxChoices})`;
  }

  /**
   * @param {object} detail
   * @returns {string}
   */
  async renderPromotion(detail) {
    const remaining = detail.remainingChoices || 0;
    const configs = w?.bxgyController?.configs?.configurations || [];
    const config = configs.find(
      (item) => String(item.id) === String(detail?.id),
    );
    const cart = w?.bxgyController?.previousCartState;
    const matchesCustomerBuys = this.cartMatchesCustomerBuys(config, cart);
    const variantIds =
      Array.isArray(detail.availableItems) && detail.availableItems.length
        ? detail.availableItems
        : config?.customerGets?.variantIds || [];
    const title = detail.name ? detail.name : `Promotion ${detail.id}`;
    const groupDisabled = !detail.eligible || !matchesCustomerBuys;
    const isSample = config.customerGets?.discountType === "sample";
    const rewardType = isSample ? "sample" : "gift";
    const rewardName = config?.rewardName;
    const selectedVariantIds = this.getSelectedVariantIds(detail);
    const statusText = matchesCustomerBuys
      ? this.getPromotionStatusText(detail, config, cart)
      : "";
    const statusClass =
      detail.eligible && matchesCustomerBuys
        ? "bxgy-choice-selector-modal__qualified-text"
        : "bxgy-choice-selector-modal__qualified-text bxgy-choice-selector-modal__qualified-text--unqualified";
    const productVariants = await Promise.all(
      variantIds.map((id) => this.findProductVariantById(id)),
    );
    const variants = productVariants.filter(Boolean);
    const itemDisabledClass = groupDisabled
      ? "bxgy-choice-selector-modal__product-item--disabled"
      : "";
    const inputType = detail.maxChoices > 1 ? "checkbox" : "radio";
    const inputDisabled = groupDisabled ? "disabled" : "";
    const isSelected = (variantId) => selectedVariantIds.has(String(variantId));
    const inputChecked = (variantId) =>
      isSelected(variantId) ? "checked" : "";

    if (isSample) {
      // Samples CTA visibility is handled by updateSamplesCta.
    }

    return `
      <div class="bxgy-selector" data-promo-id="${detail.id}">
        <div class="bxgy-choice-selector-modal__tier-header">
          ${
            config.customerGets?.discountType !== "sample" && statusText
              ? `<p class="${statusClass}">${statusText}</p><h3>Choose ${rewardName}</h3>`
              : `<h3>Choose ${rewardName}</h3>`
          }
        </div>
        <div class="bxgy-options">
          ${variants
            .map((variant) => {
              const descriptionText =
                variant.description || variant.product?.description;
              const descriptionLine = descriptionText
                ? `<p class="bxgy-choice-selector-modal__product-description">${descriptionText}</p>`
                : "";
              const optionLines =
                variant.optionValues && variant.optionValues.length
                  ? variant.optionValues
                      .map((value) => `<p>${value}</p>`)
                      .join("")
                  : variant.variantTitle
                    ? `<p>${variant.variantTitle}</p>`
                    : "";
              const itemSelectedClass = isSelected(variant.id)
                ? "bxgy-choice-selector-modal__product-item--selected"
                : "";
              const detailsDisabledClass = inputDisabled
                ? "bxgy-choice-selector-modal__product-details--disabled"
                : "";
              return `
                <label class="bxgy-choice-selector-modal__product-item ${itemDisabledClass} ${itemSelectedClass}">
                  <input type="${inputType}" name="gwp-product-${detail.id}" value="${variant.id}" ${inputDisabled} ${inputChecked(variant.id)} data-promo-id="${detail.id}" data-reward-type="${rewardType}">
                  <img class="bxgy-choice-selector-modal__product-image" src="${variant.image || "//via.placeholder.com/80x80"}" alt="${variant.title}" width="100" height="100">
                  <div class="bxgy-choice-selector-modal__product-details ${detailsDisabledClass}">
                    <p class="bxgy-choice-selector-modal__product-title">${variant.title}</p>
                    ${descriptionLine}
                    ${optionLines}
                  </div>
                </label>
              `;
            })
            .join("")}
        </div>
      </div>
    `;
  }

  /**
   * @returns {HTMLElement | null}
   */
  resetProgressBars() {
    const viewGifts = document.querySelector("[data-gwp-progress]");
    if (!viewGifts) return null;

    const container = viewGifts.querySelector(
      ".cart-drawer__progress-bar-container",
    );
    if (!container) return null;

    return this.getProgressBarStack();
  }

  /**
   * @returns {HTMLElement | null}
   */
  getProgressBarStack() {
    const viewGifts = document.querySelector("[data-gwp-progress]");
    if (!viewGifts) return null;

    const container = viewGifts.querySelector(
      ".cart-drawer__progress-bar-container",
    );
    if (!container) return null;

    let stack = container.querySelector(".cart-drawer__progress-bar-stack");
    if (!stack) {
      stack = document.createElement("div");
      stack.className = "cart-drawer__progress-bar-stack";
      container.appendChild(stack);
    }

    return stack;
  }

  /**
   * @param {object} detail
   * @param {object|null} config
   * @returns {void}
   */
  renderProgressBar(detail, config, fillPercent) {
    const amount = Number(config?.customerBuys?.minimumAmount);
    if (!Number.isFinite(amount)) return;

    const stack = this.getProgressBarStack();
    if (!stack) return;

    const cart = w?.bxgyController?.previousCartState;
    const cartTotal = this.getCartTotalForConfig(config, cart);
    const normalizedFill = Math.round(
      Math.min(Math.max(fillPercent ?? 0, 0), 100),
    );
    const markerIcon =
      cartTotal >= amount ? this.getCheckmarkSvgFilled() : null;
    const label = config?.rewardName || config?.name || "gift";

    const promoId = String(detail.id || "");
    const selector = `.cart-drawer__progress-bar-container[data-promo-progress="${promoId}"]`;
    let container = stack.querySelector(selector);
    if (!container) {
      container = document.createElement("div");
      container.className = "cart-drawer__progress-bar-container";
      container.dataset.promoProgress = promoId;
      stack.appendChild(container);
    }

    container.innerHTML = `
      ${
        markerIcon
          ? `<div class="cart-drawer__progress-bar-markers">
          <span class="cart-drawer__progress-marker" style="left: 95%;">${markerIcon}</span>
        </div>`
          : ""
      }
        <div class="cart-drawer__progress-labels">
          <div>
            <p class="gift-amount">${this.formatMoney(amount, cart?.currency || "USD")}</p>
            <p class="gift-type">${label}</p>
          </div>
        </div>
        <div class="cart-drawer__progress-bar cart-drawer__progress-bar--segmented" role="progressbar" aria-valuenow="${cartTotal}" aria-valuemin="0" aria-valuemax="${amount}">
          <span class="cart-drawer__progress-segment" style="--segment-size: 100%; --segment-fill: ${normalizedFill}%;"></span>
        </div>
      `;
  }

  /**
   * Render all promo progress bars without params.
   * @returns {void}
   */
  renderProgressBars() {
    const cart = w?.bxgyController?.previousCartState;
    if (!this.promotionStates.size) return;
    const promos = Array.from(this.promotionStates.values())
      .map((state) => state.detail)
      .filter((detail) => {
        if (!detail || detail?.type !== "choice") return false;
        const config = this.getPromotionConfig(detail);
        const matchesCustomerBuys = this.cartMatchesCustomerBuys(config, cart);
        return (
          config?.customerGets?.discountType !== "sample" &&
          config?.customerBuys?.requirementType === "minimum_purchase_amount" &&
          matchesCustomerBuys
        );
      })
      .sort(
        (a, b) =>
          this.getPromotionMinimumAmount(a) - this.getPromotionMinimumAmount(b),
      );

    const viewGiftsList = document.querySelectorAll("[data-gwp-progress]");
    const hasEligibleGifts = promos.some((detail) => detail?.eligible);
    const allGiftsAdded =
      promos.length > 0 &&
      promos.every((detail) => {
        const promoId = String(detail?.id || "");
        const maxChoices = Number(detail?.maxChoices || 0);
        if (!promoId || maxChoices <= 0) return false;
        const cartItems = Array.isArray(cart?.items) ? cart.items : [];
        const selectedCount = cartItems.reduce((total, item) => {
          return String(item?.properties?._bxgy_config_id) === promoId
            ? total + (item?.quantity || 0)
            : total;
        }, 0);
        return selectedCount >= maxChoices;
      });
    viewGiftsList.forEach((viewGifts) => {
      const shouldHide = !promos.length;
      if (shouldHide !== viewGifts.classList.contains("hidden")) {
        viewGifts.classList.toggle("hidden", shouldHide);
      }
      const link = viewGifts.querySelector(".cart-drawer__view-gifts-link");
      if (link) {
        link.textContent =
          allGiftsAdded || !hasEligibleGifts ? "View Gifts" : "Add Gifts";
      }
    });

    if (!promos.length) {
      return;
    }

    const stack = this.getProgressBarStack();
    if (!stack) return;
    const maxVisible = 3;
    const visiblePromos = promos.slice(0, maxVisible);
    const extraCount = Math.max(0, promos.length - maxVisible);
    const activePromoIds = new Set(
      visiblePromos.map((detail) => String(detail.id)),
    );
    if (extraCount > 0) {
      activePromoIds.add("more");
    }

    const cartTotal = (cart?.items_subtotal_price || 0) / 100;
    const minAmounts = visiblePromos.map((detail) =>
      this.getPromotionMinimumAmount(detail),
    );

    const activeIndex = minAmounts.findIndex((amount) => cartTotal <= amount);
    const currentIndex = activeIndex === -1 ? promos.length - 1 : activeIndex;

    visiblePromos.forEach((detail, index) => {
      const config = this.getPromotionConfig(detail);
      const amount = minAmounts[index];
      let fillPercent = 0;
      if (index < currentIndex) {
        fillPercent = 100;
      } else if (index === currentIndex) {
        const qualifyingTotal = this.getCartTotalForConfig(config, cart);
        fillPercent = amount > 0 ? (qualifyingTotal / amount) * 100 : 0;
      }

      this.renderProgressBar(detail, config, fillPercent);
    });

    if (extraCount > 0) {
      const remainingAmounts = promos
        .slice(maxVisible)
        .map((detail) => this.getPromotionMinimumAmount(detail))
        .filter((amount) => Number.isFinite(amount));
      const lastVisibleAmount = minAmounts[minAmounts.length - 1] ?? 0;
      const maxRemainingAmount =
        remainingAmounts.length > 0 ? Math.max(...remainingAmounts) : 0;
      let moreFill = 0;
      if (maxRemainingAmount > lastVisibleAmount) {
        const remainingProgress =
          (cartTotal - lastVisibleAmount) /
          (maxRemainingAmount - lastVisibleAmount);
        moreFill = Math.round(
          Math.min(Math.max(remainingProgress * 100, 0), 100),
        );
      } else if (cartTotal >= maxRemainingAmount && maxRemainingAmount > 0) {
        moreFill = 100;
      }
      this.renderMoreProgressBar(extraCount, moreFill);
    }

    Array.from(
      stack.querySelectorAll(".cart-drawer__progress-bar-container"),
    ).forEach((container) => {
      const promoId = container.dataset.promoProgress || "";
      if (!activePromoIds.has(promoId)) {
        container.remove();
      }
    });
  }

  /**
   * @param {number} extraCount
   * @returns {void}
   */
  renderMoreProgressBar(extraCount, fillPercent = 0) {
    const stack = this.getProgressBarStack();
    if (!stack) return;

    const selector =
      '.cart-drawer__progress-bar-container[data-promo-progress="more"]';
    let container = stack.querySelector(selector);
    if (!container) {
      container = document.createElement("div");
      container.className = "cart-drawer__progress-bar-container";
      container.dataset.promoProgress = "more";
      stack.appendChild(container);
    }

    const normalizedFill = Math.round(
      Math.min(Math.max(fillPercent ?? 0, 0), 100),
    );

    container.innerHTML = `
      <div class="cart-drawer__progress-labels">
        <div>
          <p class="gift-amount">+${extraCount}</p>
          <p class="gift-type">More</p>
        </div>
      </div>
      <div class="cart-drawer__progress-bar cart-drawer__progress-bar--segmented" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100">
        <span class="cart-drawer__progress-segment" style="--segment-size: 100%; --segment-fill: ${normalizedFill}%;"></span>
      </div>
    `;
  }

  /**
   * @param {object} detail
   * @returns {number}
   */
  getPromotionMinimumAmount(detail) {
    const config = this.getPromotionConfig(detail);
    const minimumAmount = config?.customerBuys?.minimumAmount;
    const parsed = Number(minimumAmount);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  }

  /**
   * @param {object} detail
   * @returns {string}
   */
  getPromotionStatusText(detail, config, cart) {
    const resolvedConfig = config || this.getPromotionConfig(detail);
    const minimumAmount = Number(resolvedConfig?.customerBuys?.minimumAmount);
    if (!Number.isFinite(minimumAmount)) return "";

    const resolvedCart = cart || w?.bxgyController?.previousCartState;
    const cartTotal = this.getCartTotalForConfig(resolvedConfig, resolvedCart);
    const currency = resolvedCart?.currency || "USD";
    const remaining = Math.max(0, minimumAmount - cartTotal);

    return remaining === 0
      ? `You've qualified`
      : `Spend ${this.formatMoney(remaining, currency)} more for your next gift`;
  }

  /**
   * @param {object} detail
   * @returns {object|null}
   */
  getPromotionConfig(detail) {
    const configs = w?.bxgyController?.configs?.configurations || [];
    return (
      configs.find((item) => String(item.id) === String(detail?.id)) || null
    );
  }

  /**
   * @param {object} detail
   * @returns {Set<string>}
   */
  getSelectedVariantIds(detail) {
    const cart = w?.bxgyController?.previousCartState;
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const promoId = String(detail?.id);
    const selected = items
      .filter((item) => String(item?.properties?._bxgy_config_id) === promoId)
      .map((item) => String(item?.variant_id));
    return new Set(selected);
  }

  /**
   * @param {BxgyConfiguration[]} configurations
   * @param {Cart} cart
   * @returns {void}
   */
  renderUnlockMessages(configurations, cart) {
    const unlockList = this.querySelector(
      ".bxgy-choice-selector-modal__unlock-list",
    );
    if (!unlockList) return;

    const currency = cart?.currency || "USD";
    const eligiblePromos = configurations
      .filter(
        (config) =>
          config.customerGets?.rewardStrategy === "choice" &&
          config.customerGets?.discountType !== "sample",
      )
      .sort((a, b) => {
        const aAmount = Number(a?.customerBuys?.minimumAmount);
        const bAmount = Number(b?.customerBuys?.minimumAmount);
        const safeA = Number.isFinite(aAmount)
          ? aAmount
          : Number.MAX_SAFE_INTEGER;
        const safeB = Number.isFinite(bAmount)
          ? bAmount
          : Number.MAX_SAFE_INTEGER;
        return safeA - safeB;
      });

    unlockList.innerHTML = eligiblePromos
      .map((promo) => {
        const promoState = this.promotionStates.get(promo.id);
        const meetsRules = Boolean(promoState?.detail?.eligible);
        const matchesCustomerBuys = this.cartMatchesCustomerBuys(promo, cart);
        const checkmark = `<span class="bxgy-choice-selector-modal__checkmark">${meetsRules ? this.getCheckmarkSvgFilled() : this.getCheckmarkSvgUnfilled()}</span>`;
        if (!matchesCustomerBuys) {
          return "";
        }
        return `<p>${checkmark}Spend ${this.formatMoney(promo.customerBuys.minimumAmount, currency)} for a free gift</p>`;
      })
      .join("");
  }

  getCustomerBuysVariantIds(config) {
    const rawIds = config?.customerBuys?.variantIds;
    if (!rawIds) return [];
    if (Array.isArray(rawIds)) return rawIds;
    if (typeof rawIds === "string") {
      try {
        const parsed = JSON.parse(rawIds);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }
    return [];
  }

  cartMatchesCustomerBuys(config, cart) {
    const variantIds = this.getCustomerBuysVariantIds(config);
    if (!variantIds.length) return true;
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const allowed = new Set(variantIds.map((id) => String(id)));
    return items.some((item) => allowed.has(String(item?.variant_id)));
  }

  getCartTotalForConfig(config, cart) {
    const variantIds = this.getCustomerBuysVariantIds(config);
    if (!variantIds.length) {
      return (cart?.total_price || 0) / 100;
    }

    const items = Array.isArray(cart?.items) ? cart.items : [];
    const allowed = new Set(variantIds.map((id) => String(id)));
    const total = items.reduce((sum, item) => {
      if (!allowed.has(String(item?.variant_id))) return sum;
      const linePrice =
        item?.final_line_price ??
        Number(item?.final_price || 0) * Number(item?.quantity || 0);
      return sum + Number(linePrice || 0);
    }, 0);

    return total / 100;
  }

  /**
   * @param {number} variantId
   * @returns {Promise<Product | null>}
   */
  async findProductVariantById(variantId) {
    const cached = this.variantCache.get(String(variantId));
    if (cached) return cached;

    const fetchPromise = (async () => {
      try {
        const [variant] = await fetchShopifyResources({
          ids: [variantId],
          type: "ProductVariant",
        });
        if (!variant || !variant.product) return null;

        const image = variant.image || variant.product.featuredImage;
        const optionValues = Array.isArray(variant.selectedOptions)
          ? variant.selectedOptions
              .map((option) => option.value)
              .filter(Boolean)
          : [];
        return {
          id: this.extractNumericId(variant.id) || variant.id,
          title: variant.product.title,
          image: image ? image.url : "",
          description: variant.product.description || "",
          variantTitle:
            variant.title && variant.title !== "Default Title"
              ? variant.title
              : "",
          optionValues,
        };
      } catch (error) {
        console.error("[BxgyChoiceSelector] Failed to fetch variant:", error);
        return null;
      }
    })();

    this.variantCache.set(String(variantId), fetchPromise);
    return fetchPromise;
  }

  /**
   * @param {string} gid
   * @returns {string | null}
   */
  extractNumericId(gid) {
    if (!gid || typeof gid !== "string") return null;
    if (!gid.startsWith("gid://")) return gid;
    const parts = gid.split("/");
    return parts.length ? parts[parts.length - 1] : null;
  }

  /**
   * @returns {string}
   */
  getCheckmarkSvgFilled() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M7.00425 10.9843L11.721 6.26759L10.9817 5.5283L7.00425 9.50572L5.00475 7.50622L4.26546 8.24551L7.00425 10.9843ZM8.00119 14.665C7.07931 14.665 6.21281 14.49 5.40166 14.1402C4.59052 13.7903 3.88497 13.3155 3.285 12.7158C2.68503 12.1161 2.21001 11.4108 1.85992 10.6C1.50995 9.78922 1.33496 8.92295 1.33496 8.00119C1.33496 7.07931 1.50989 6.21281 1.85974 5.40166C2.2096 4.59052 2.68439 3.88497 3.28412 3.285C3.88386 2.68503 4.58912 2.21001 5.39991 1.85992C6.2107 1.50995 7.07697 1.33496 7.99873 1.33496C8.92061 1.33496 9.78712 1.50989 10.5983 1.85974C11.4094 2.2096 12.115 2.68439 12.7149 3.28412C13.3149 3.88386 13.7899 4.58912 14.14 5.39991C14.49 6.2107 14.665 7.07697 14.665 7.99873C14.665 8.92061 14.49 9.78712 14.1402 10.5983C13.7903 11.4094 13.3155 12.115 12.7158 12.7149C12.1161 13.3149 11.4108 13.7899 10.6 14.14C9.78922 14.49 8.92295 14.665 8.00119 14.665Z" fill="#005600"/>
      </svg>
    `;
  }

  /**
   * @returns {string}
   */
  getCheckmarkSvgUnfilled() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8.01681 14.7167C7.09494 14.7167 6.22843 14.5418 5.41729 14.1919C4.60615 13.8421 3.90059 13.3673 3.30062 12.7676C2.70066 12.1678 2.22563 11.4626 1.87554 10.6518C1.52557 9.84098 1.35059 8.9747 1.35059 8.05295C1.35059 7.13107 1.52551 6.26456 1.87537 5.45342C2.22522 4.64228 2.70001 3.93672 3.29975 3.33676C3.89948 2.73679 4.60474 2.26176 5.41553 1.91168C6.22633 1.5617 7.0926 1.38672 8.01436 1.38672C8.93623 1.38672 9.80274 1.56165 10.6139 1.9115C11.425 2.26135 12.1306 2.73615 12.7305 3.33588C13.3305 3.93561 13.8055 4.64088 14.1556 5.45167C14.5056 6.26246 14.6806 7.12873 14.6806 8.05049C14.6806 8.97237 14.5057 9.83887 14.1558 10.65C13.806 11.4612 13.3312 12.1667 12.7314 12.7667C12.1317 13.3666 11.4264 13.8417 10.6156 14.1918C9.80485 14.5417 8.93857 14.7167 8.01681 14.7167ZM8.01559 13.6643C9.58245 13.6643 10.9096 13.1206 11.997 12.0332C13.0845 10.9457 13.6282 9.61858 13.6282 8.05172C13.6282 6.48486 13.0845 5.15771 11.997 4.07026C10.9096 2.98281 9.58245 2.43909 8.01559 2.43909C6.44873 2.43909 5.12157 2.98281 4.03413 4.07026C2.94668 5.15771 2.40295 6.48486 2.40295 8.05172C2.40295 9.61858 2.94668 10.9457 4.03413 12.0332C5.12157 13.1206 6.44873 13.6643 8.01559 13.6643Z" fill="#666666"/>
      </svg>
    `;
  }

  /**
   * @param {number} amount
   * @param {string} currency
   * @returns {string}
   */
  formatMoney(amount, currency = "USD") {
    if (!Number.isFinite(amount)) return "";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (error) {
      return `$${amount}`;
    }
  }
}

customElements.define("bxgy-choice-selector", BxgyChoiceSelector);
